import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Korean mirror of the police clearance certificate guide. */
const TITLE = "태국 범죄경력증명서(Police Clearance) 해외 사용 — 완전 가이드";
const DESC =
  "태국 범죄경력증명서 신청 방법, 필요 서류, 이후의 영사국·대사관 인증 절차, 그리고 반송으로 이어지는 대표적인 사유를 정리했습니다.";
const PATH = "/blog/police-clearance-certificate-guide";
const URL = `/ko${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "태국 범죄경력증명서는 어디에서 발급합니까?",
    answer:
      "태국 왕립경찰청(Royal Thai Police) 범죄기록과에서 발급합니다. 본인이 직접 방문하거나, 규정에 따라 대리인이 신청할 수 있습니다.",
  },
  {
    question: "어떤 서류를 준비해야 합니까?",
    answer:
      "여권 또는 신분증, 주소·거주 관련 서류, 지문 채취 양식, 신청서, 그리고 사용 목적을 설명하는 자료(비자 요구 서한, 고용주 서한, 입학 허가서 등)가 일반적입니다.",
  },
  {
    question: "처리 기간은 얼마나 걸립니까?",
    answer:
      "조회 범위와 보완 요청 여부에 따라 며칠에서 몇 주까지 걸립니다. 영사국과 대사관 인증까지 필요하면 추가 기간을 확보해야 합니다.",
  },
  {
    question: "증명서에 유효기간이 있습니까?",
    answer:
      "발급 기관이 유효기간을 표시하지 않는 경우가 많지만, 제출 기관은 보통 발급 후 3~6개월 이내를 요구합니다. 제출 기관의 서면 요건을 기준으로 하십시오.",
  },
  {
    question: "번역과 인증도 필요합니까?",
    answer:
      "해외에서 사용하려면 통상 목적지 언어로 번역한 뒤, 태국 외교부 영사국 확인과 해당국 주태 대사관·영사관 확인을 순서대로 받습니다. 헤이그 협약국은 경로가 다를 수 있으니 먼저 확인하십시오.",
  },
  {
    question: "태국 밖에서도 신청할 수 있습니까?",
    answer:
      "가능합니다. 현지 태국 대사관·영사관을 통해 제출하거나, 인증된 위임장으로 태국 내 대리인이 진행합니다. 지문 채취 방식은 접수 창구에 먼저 확인하십시오.",
  },
  {
    question: "반송의 가장 흔한 사유는 무엇입니까?",
    answer:
      "여권과 이름 표기 불일치, 지문 양식 불선명, 사용 목적 미기재, 발급 시점 초과, 그리고 인증 순서 착오입니다.",
  },
];

export const Route = createFileRoute("/ko/blog/police-clearance-certificate-guide")({
  component: KoPccGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ko" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("police-clearance-certificate-guide", "ko") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "태국 범죄경력증명서, police clearance 태국, 태국 신원증명서 번역, 태국 영사국 인증, 왕립경찰청 증명서",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ko"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "홈", url: "/ko" },
              { name: "가이드", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("police-clearance-certificate-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Police Clearance",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: string[] = [
  "여권 원본(해외 사용 시 여권 표기를 기준으로 함)",
  "태국 신분증 또는 체류 증명(해당하는 경우)",
  "주소 등록 자료 또는 거주 증명",
  "지문 채취 양식(접수 창구가 지정한 형식)",
  "사용 목적 자료: 비자 요구 서한, 고용주 서한, 입학 허가서",
  "대리 진행 시: 인증된 위임장과 대리인 신분증",
];

const STEPS: string[] = [
  "제출 기관에 필요한 판본·언어·발급 시점 요건을 먼저 확인",
  "신분·주소 자료와 사용 목적 자료를 준비",
  "지문을 채취하고 신청서를 제출(본인 또는 규정상 대리인)",
  "증명서를 수령한 뒤 이름 표기·생년월일·발급일을 확인",
  "목적지 요건에 맞게 번역하고 외교부 영사국 확인을 받음",
  "해당국 주태 대사관·영사관 확인을 받은 뒤 제출",
];

const MISTAKES: string[] = [
  "이름 표기가 여권과 달라 동일인 확인이 되지 않는 경우",
  "요건 확인 전에 번역·인증을 먼저 진행해 순서가 어긋난 경우",
  "지문 양식이 불선명하거나 지정 형식과 다른 경우",
  "발급 시점이 오래되어 제출 기관의 시한을 넘긴 경우",
  "원본이나 인증 사본 대신 일반 복사본을 제출한 경우",
];

function KoPccGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ko" className="text-muted-foreground hover:text-foreground">
          홈
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          가이드
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="police-clearance-certificate-guide" locale="ko" />
      <p className="answer-summary speakable-summary">
        태국 범죄경력증명서는 왕립경찰청에서 발급하며, 신분 서류·주소 자료·지문 양식·사용 목적 자료가 필요합니다.
        해외에서 사용할 때는 통상 번역 후 태국 외교부 영사국 확인과 목적지국 주태 대사관·영사관 확인을 순서대로 받습니다.
        제출 기관의 언어와 발급 시점 요건을 먼저 확인하면 재작업을 피할 수 있습니다.
      </p>

      <h2>1. 준비할 서류</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>

      <h2>2. 진행 순서</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 반송의 흔한 사유</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. 자주 묻는 질문</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 참고 자료와 관련 서비스</h2>
      <p className="text-sm">
        참고:{" "}
        <a href="https://www.royalthaipolice.go.th/" target="_blank" rel="noreferrer">
          태국 왕립경찰청
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          태국 외교부 영사국
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ko/pcc" className="text-primary underline">
            범죄경력증명서 대행 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/lgz" className="text-primary underline">
            서류 인증·영사확인 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/sworn" className="text-primary underline">
            법원 등록 번역 서비스
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        내용 검토일 {PUBLISHED}. 본 가이드는 계획 수립을 위한 일반 정보이며 기관의 결정을 대신하지 않습니다. 진행 전 제출 기관의 최신 요건을 확인하십시오.
      </p>
    </article>
  );
}
